import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext.tsx';
import { Alert, Button, Field, Spinner } from '../../components/ui.tsx';
import { ApiError } from '../../lib/api.ts';
import { logoUrl, useBranding } from '../../lib/profile-queries.ts';

export function LoginPage() {
  const { login, completeTotp } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (challengeToken) {
        await completeTotp(challengeToken, code);
        landOnHome();
      } else {
        const result = await login(email, password);
        if (result.status === 'totp_required') setChallengeToken(result.challengeToken);
        else landOnHome();
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.',
      );
      // A failed second factor sends the user back to the start: the challenge
      // is single-use and has already been consumed server-side.
      if (challengeToken) {
        setChallengeToken(null);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Signing in always arrives at the top, wherever the top is for this person.
   *
   * Otherwise the URL from before the session expired is still in the bar, and
   * the user lands mid-task on a page they did not ask for — or, after a
   * bookmark to a deleted record, on an error.
   *
   * The route is left to decide which page that is. A Viewer has no dashboard
   * to land on, and the role has only just arrived — asking for it here would
   * read the profile from a render that has not seen it yet — so this goes to
   * "/" and the catch-all sends it on once the session has settled.
   */
  function landOnHome() {
    navigate('/', { replace: true });
  }

  const logo = logoUrl(branding.data);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <img
            src={logo ?? '/brand/logo.png'}
            alt="Abdul Qadir Finance System"
            className="h-24 w-auto object-contain"
          />
        </div>

        <div className="rounded-xl border border-border-strong bg-surface p-6 shadow-md">
          <h1 className="text-h2 text-ink">{challengeToken ? 'Verification code' : 'Sign in'}</h1>
          <p className="mt-1 text-body-sm text-muted">
            {challengeToken
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Access to this system is restricted and monitored.'}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {error ? <Alert>{error}</Alert> : null}

            {challengeToken ? (
              <Field
                label="Authentication code"
                hint="Or a backup code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                data-numeric
              />
            ) : (
              <>
                <Field
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                />
                <Field
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </>
            )}

            <Button type="submit" variant="primary" disabled={busy} className="h-control-lg w-full">
              {busy ? <Spinner label="Signing in" /> : challengeToken ? 'Verify' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-micro text-muted">
          Abdul Qadir Finance System · Track · Manage · Grow
        </p>
      </div>
    </div>
  );
}
